import { useLayoutEffect, useRef, useState } from 'react'
import { defaultRangeExtractor, useVirtualizer } from '@tanstack/react-virtual'

export function useChatsVirtualizer(rows: { id: string }[], scrollElement: HTMLDivElement | null) {
  const listRef = useRef<HTMLUListElement>(null)
  const [firstRow, setFirstRow] = useState<HTMLLIElement | null>(null)
  const [scrollMargin, setScrollMargin] = useState(0)
  const [rowHeight, setRowHeight] = useState(52)
  const [focusedId, setFocusedId] = useState<string | null>(null)
  const [menuId, setMenuId] = useState<string | null>(null)

  // The heading, search, and any error message scroll with the rows. Measure where
  // the list begins rather than assuming a fixed header height.
  useLayoutEffect(() => {
    const scroll = scrollElement
    const list = listRef.current
    if (!scroll || !list || !firstRow) return
    const measure = () => {
      setScrollMargin(list.getBoundingClientRect().top - scroll.getBoundingClientRect().top + scroll.scrollTop)
      const height = firstRow.getBoundingClientRect().height
      if (height) setRowHeight(height)
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(scroll)
    observer.observe(firstRow)
    if (list.parentElement) observer.observe(list.parentElement)
    return () => observer.disconnect()
  }, [scrollElement, firstRow])

  const virtualizer = useVirtualizer<HTMLDivElement, HTMLLIElement>({
    count: rows.length,
    getScrollElement: () => scrollElement,
    getItemKey: (index) => rows[index].id,
    estimateSize: () => rowHeight,
    scrollMargin,
    overscan: 5,
    rangeExtractor: (range) => {
      const indexes = new Set([...defaultRangeExtractor(range), 0, rows.length - 1])
      const focusedIndex = rows.findIndex((row) => row.id === focusedId)
      if (focusedIndex !== -1) {
        for (const index of [focusedIndex - 1, focusedIndex, focusedIndex + 1]) {
          if (index >= 0 && index < rows.length) indexes.add(index)
        }
      }
      const menuIndex = rows.findIndex((row) => row.id === menuId)
      if (menuIndex !== -1) indexes.add(menuIndex)
      return [...indexes].sort((a, b) => a - b)
    },
  })

  // Every row has the same layout. Apply its measured height to unmounted rows
  // too, so jumping to the end is accurate on mobile and after resizing.
  useLayoutEffect(() => virtualizer.measure(), [rowHeight, virtualizer])

  return { listRef, setFirstRow, virtualizer, scrollMargin, setFocusedId, setMenuId }
}
